import type { LscArchiveItem, LscLiveLeagueGame, LscMatch, LscMatchSummary, LscLeague } from '../../types';
import { displaySeasonBesideActiveEdition } from '../../seasonDisplay';
import { formatAwayAtHome } from '../../leagueNav';
import {
  isQualifiedLikelyLiveGame,
  isQualifiedMainGame,
  isVerifiedLiveGame,
} from '../LiveLeagueGamesCard';

export const MEDIA_ROW_MAX_CARDS = 8;

/** Media Home live hero always uses member-visible qualified rows only. */
export function getMediaHomeLiveGames(
  memberVisibleRows: LscLiveLeagueGame[]
): LscLiveLeagueGame[] {
  return memberVisibleRows.filter(isQualifiedMainGame);
}

export type MediaHomeLivePartition = {
  verifiedGames: LscLiveLeagueGame[];
  likelyGames: LscLiveLeagueGame[];
  featuredGame: LscLiveLeagueGame | null;
  secondaryGames: LscLiveLeagueGame[];
  allLiveGames: LscLiveLeagueGame[];
};

/**
 * Prioritize verified live games for the featured hero.
 * Likely games appear as secondary cards when verified games exist.
 */
export function partitionMediaHomeLiveGames(
  memberVisibleRows: LscLiveLeagueGame[]
): MediaHomeLivePartition {
  const qualified = getMediaHomeLiveGames(memberVisibleRows);
  const verifiedGames = qualified.filter(isVerifiedLiveGame);
  const likelyGames = qualified.filter(isQualifiedLikelyLiveGame);

  let featuredGame: LscLiveLeagueGame | null = null;
  let secondaryGames: LscLiveLeagueGame[] = [];

  if (verifiedGames.length > 0) {
    featuredGame = verifiedGames[0];
    secondaryGames = [...verifiedGames.slice(1), ...likelyGames];
  } else if (likelyGames.length > 0) {
    featuredGame = likelyGames[0];
    secondaryGames = likelyGames.slice(1);
  }

  return {
    verifiedGames,
    likelyGames,
    featuredGame,
    secondaryGames,
    allLiveGames: [...verifiedGames, ...likelyGames],
  };
}

export function isLikelyOnlyFeaturedGame(game: LscLiveLeagueGame | null): boolean {
  if (!game) return false;
  return isQualifiedLikelyLiveGame(game) && !isVerifiedLiveGame(game);
}

/** True when the hero is showing live content with no verified games. */
export function isLikelyOnlyLiveHeroState(
  liveGames: LscLiveLeagueGame[],
  featuredGame: LscLiveLeagueGame | null
): boolean {
  if (!featuredGame) return false;
  return !liveGames.some(isVerifiedLiveGame) && isLikelyOnlyFeaturedGame(featuredGame);
}

export function getLiveHeroSectionHeading(
  liveGames: LscLiveLeagueGame[],
  featuredGame: LscLiveLeagueGame | null
): string {
  if (isLikelyOnlyLiveHeroState(liveGames, featuredGame)) {
    return 'Likely Live';
  }
  return 'Live Now';
}

export function getLiveHeroSectionSubtitle(
  liveGames: LscLiveLeagueGame[],
  featuredGame: LscLiveLeagueGame | null
): string {
  if (isLikelyOnlyLiveHeroState(liveGames, featuredGame)) {
    return 'Verification in progress — a possible live stream was detected.';
  }
  return 'Qualified league games from connected broadcaster streams.';
}

export function getLiveHeroExternalCtaLabel(game: LscLiveLeagueGame): string {
  if (isVerifiedLiveGame(game)) {
    return 'Watch live on Twitch';
  }
  return 'View on Twitch';
}

export function matchHasPublishedArchive(
  matchId: string | undefined,
  archivesByMatchId: Map<string, LscArchiveItem[]>
): boolean {
  if (!matchId) return false;
  const linked = archivesByMatchId.get(matchId) ?? [];
  return linked.some((item) => item.archive_status === 'published');
}

export type DerivedUpcomingMatches = {
  future: LscMatch[];
  currentSlate: LscMatch[];
};

/**
 * Schedule-only upcoming derivation for Media Home.
 *
 * Rules:
 * - Scope to the league document's active season and, when set, active edition.
 *   There is no latest-season fallback and a blank match edition does not match
 *   a declared active edition.
 * - `future` (Upcoming Games): only active matches with scheduled_at >= now.
 *   Matches without scheduled_at never belong in `future`.
 * - `currentSlate` (This Week / Current Slate): active-stage matches
 *   without a published archive that were not already placed in `future`.
 * - Never infer live status from schedule fields here.
 */
export function deriveUpcomingMatches(
  matches: LscMatch[],
  archivesByMatchId: Map<string, LscArchiveItem[]>,
  league: Pick<LscLeague, 'active_season_label' | 'active_stage_label' | 'active_edition'>,
  now: Date = new Date()
): DerivedUpcomingMatches {
  const activeSeason = (league.active_season_label ?? '').trim();
  if (!activeSeason) {
    return { future: [], currentSlate: [] };
  }
  const activeEdition = (league.active_edition ?? '').trim().toLowerCase();
  const seasonScopedMatches = matches.filter((match) => {
    if ((match.season_label ?? '').trim() !== activeSeason) return false;
    if (!activeEdition) return true;
    return (match.edition ?? '').trim().toLowerCase() === activeEdition;
  });
  return deriveUpcomingMatchesForStage(
    seasonScopedMatches,
    archivesByMatchId,
    league.active_stage_label,
    now
  );
}

export function deriveUpcomingMatchesForStage(
  matches: LscMatch[],
  archivesByMatchId: Map<string, LscArchiveItem[]>,
  activeStageLabel?: string | null,
  now: Date = new Date()
): DerivedUpcomingMatches {
  const nowMs = now.getTime();
  const activeStage = (activeStageLabel ?? '').trim().toLowerCase();

  const future: LscMatch[] = [];
  const currentSlate: LscMatch[] = [];
  const seenIds = new Set<string>();

  for (const match of matches) {
    if ((match.lifecycle_status ?? 'active') !== 'active') continue;
    if (matchHasPublishedArchive(match.id, archivesByMatchId)) continue;

    const scheduledAt = match.scheduled_at?.trim();
    if (!scheduledAt) {
      continue;
    }
    const scheduledMs = Date.parse(scheduledAt);
    if (!Number.isNaN(scheduledMs) && scheduledMs >= nowMs) {
      future.push(match);
      seenIds.add(match.id);
    }
  }

  future.sort((left, right) => {
    const leftMs = Date.parse(left.scheduled_at ?? '');
    const rightMs = Date.parse(right.scheduled_at ?? '');
    if (Number.isNaN(leftMs) && Number.isNaN(rightMs)) return 0;
    if (Number.isNaN(leftMs)) return 1;
    if (Number.isNaN(rightMs)) return -1;
    return leftMs - rightMs;
  });

  if (activeStage) {
    for (const match of matches) {
      if (seenIds.has(match.id)) continue;
      if ((match.lifecycle_status ?? 'active') !== 'active') continue;
      if (matchHasPublishedArchive(match.id, archivesByMatchId)) continue;
      const stage = (match.stage_label ?? '').trim().toLowerCase();
      if (stage && stage === activeStage) {
        currentSlate.push(match);
        seenIds.add(match.id);
      }
    }
  }

  return { future, currentSlate };
}

export function getScheduleCardLabel(
  match: LscMatch,
  bucket: 'future' | 'currentSlate'
): string {
  if (bucket === 'future') {
    return 'Scheduled';
  }
  if (match.scheduled_at?.trim()) {
    return 'This Week';
  }
  return 'Awaiting stream';
}

export function dedupeArchivesById(archives: LscArchiveItem[]): LscArchiveItem[] {
  const seen = new Set<string>();
  const deduped: LscArchiveItem[] = [];
  for (const archive of archives) {
    if (seen.has(archive.id)) continue;
    seen.add(archive.id);
    deduped.push(archive);
  }
  return deduped;
}

export function archiveHasUsefulMediaContent(archive: LscArchiveItem): boolean {
  if (archive.thumbnail_url?.trim()) return true;
  if (archive.title?.trim()) return true;
  if (archive.match_summary?.home_side_label?.trim()) return true;
  if (archive.match_summary?.away_side_label?.trim()) return true;
  return false;
}

export function formatArchiveMatchup(
  summary?: LscMatchSummary | null,
  fallback?: Pick<LscMatch, 'home_side_label' | 'away_side_label'>
): string {
  if (summary?.home_side_label || summary?.away_side_label) {
    return formatAwayAtHome({
      away_side_label: summary.away_side_label,
      home_side_label: summary.home_side_label,
    });
  }
  if (fallback) {
    return formatAwayAtHome(fallback);
  }
  return 'League game';
}

export function formatArchiveStageLabel(
  summary?: LscMatchSummary | null,
  activeEdition?: string | null
): string | null {
  const parts = [
    summary?.stage_label?.trim(),
    displaySeasonBesideActiveEdition(
      summary?.season_label,
      summary?.edition,
      activeEdition
    ),
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** Normalize Twitch VOD thumbnail template placeholders before rendering. */
export function normalizeTwitchThumbnailUrl(
  url: string | null | undefined,
  width = 640,
  height = 360
): string | null {
  const trimmed = url?.trim();
  if (!trimmed) return null;
  return trimmed
    .replace(/%\{width\}/g, String(width))
    .replace(/%\{height\}/g, String(height))
    .replace(/\{width\}/g, String(width))
    .replace(/\{height\}/g, String(height));
}

function hasArchiveMatchup(summary?: LscMatchSummary | null): boolean {
  return Boolean(summary?.home_side_label?.trim() || summary?.away_side_label?.trim());
}

/** Member-facing archive card title — never raw Twitch VOD title. */
export function getArchiveDisplayTitle(archive: LscArchiveItem): string {
  if (hasArchiveMatchup(archive.match_summary)) {
    return formatArchiveMatchup(archive.match_summary);
  }
  const stage = archive.match_summary?.stage_label?.trim();
  if (stage) {
    return `${stage} Replay`;
  }
  return 'League Replay';
}

function formatEditionLabel(edition?: string | null): string | null {
  const raw = (edition || '').trim().toLowerCase();
  if (!raw) return null;
  const match = /^(madden|cfb)-(\d{2})$/.exec(raw);
  if (!match) return edition?.trim() || null;
  return match[1] === 'cfb' ? `CFB ${match[2]}` : `Madden ${match[2]}`;
}

export function formatArchiveCardMeta(
  archive: LscArchiveItem,
  activeEdition?: string | null
): string {
  const rowEdition = archive.match_summary?.edition;
  const active = (activeEdition || '').trim().toLowerCase();
  const row = (rowEdition || '').trim().toLowerCase();
  const season = displaySeasonBesideActiveEdition(
    archive.match_summary?.season_label,
    rowEdition,
    activeEdition
  );
  const editionAlreadyPrefixed = Boolean(active && row && row !== active);
  const hideTrailingEdition = Boolean(active && (!row || row === active));
  const parts = [
    archive.match_summary?.stage_label?.trim(),
    season,
    editionAlreadyPrefixed || hideTrailingEdition ? null : formatEditionLabel(rowEdition),
    archive.provider_broadcaster_login?.trim()
      ? `@${archive.provider_broadcaster_login.trim()}`
      : null,
  ].filter(Boolean);
  return parts.join(' · ');
}

export function filterArchivesForThisWeek(
  archives: LscArchiveItem[],
  activeStageLabel?: string | null
): LscArchiveItem[] {
  const activeStage = (activeStageLabel ?? '').trim().toLowerCase();
  if (!activeStage) return [];
  return dedupeArchivesById(
    archives.filter((item) => {
      if (!archiveHasUsefulMediaContent(item)) return false;
      const stage = (item.match_summary?.stage_label ?? '').trim().toLowerCase();
      return stage === activeStage;
    })
  ).slice(0, MEDIA_ROW_MAX_CARDS);
}

export type TeamArchiveGroup = {
  teamLabel: string;
  items: LscArchiveItem[];
};

export function groupArchivesByTeam(archives: LscArchiveItem[]): TeamArchiveGroup[] {
  const map = new Map<string, LscArchiveItem[]>();
  for (const item of archives) {
    if (!archiveHasUsefulMediaContent(item)) continue;
    const home = item.match_summary?.home_side_label?.trim();
    const away = item.match_summary?.away_side_label?.trim();
    const labels = [home, away].filter(Boolean) as string[];
    for (const label of labels) {
      const bucket = map.get(label) ?? [];
      if (bucket.some((existing) => existing.id === item.id)) {
        continue;
      }
      bucket.push(item);
      map.set(label, bucket);
    }
  }
  return Array.from(map.entries())
    .map(([teamLabel, items]) => ({
      teamLabel,
      items: items.slice(0, MEDIA_ROW_MAX_CARDS),
    }))
    .filter((group) => group.items.length > 0)
    .sort((left, right) => left.teamLabel.localeCompare(right.teamLabel))
    .slice(0, 6);
}

export type BroadcasterArchiveGroup = {
  broadcasterLogin: string;
  items: LscArchiveItem[];
};

export function groupArchivesByBroadcaster(
  archives: LscArchiveItem[]
): BroadcasterArchiveGroup[] {
  const map = new Map<string, LscArchiveItem[]>();
  for (const item of archives) {
    const login = item.provider_broadcaster_login?.trim();
    if (!login || !archiveHasUsefulMediaContent(item)) continue;
    const bucket = map.get(login) ?? [];
    if (bucket.some((existing) => existing.id === item.id)) {
      continue;
    }
    bucket.push(item);
    map.set(login, bucket);
  }
  return Array.from(map.entries())
    .map(([broadcasterLogin, items]) => ({
      broadcasterLogin,
      items: items.slice(0, MEDIA_ROW_MAX_CARDS),
    }))
    .filter((group) => group.items.length > 0)
    .sort((left, right) => left.broadcasterLogin.localeCompare(right.broadcasterLogin))
    .slice(0, 6);
}
